import { useQuery } from "@apollo/client";
import {
  Container,
  CustomThemeProvider,
  EmptyState,
  Loading,
} from "@fluidattacks/design";
import _ from "lodash";
import isNull from "lodash/isNull";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { useTheme } from "styled-components";

import { GET_GROUP_FINDINGS } from "../findings/queries";
import { Charts } from "features/charts";
import { Logger } from "utils/logger";

const GroupAnalytics: React.FC = (): JSX.Element => {
  const { t } = useTranslation();

  const params = useParams() as { groupName: string };
  const searchParams = new URLSearchParams(useLocation().search);
  const theme = useTheme();

  const subjectFromSearchParams = searchParams.get("group");
  const subject = isNull(subjectFromSearchParams)
    ? params.groupName
    : subjectFromSearchParams;

  const { data, loading } = useQuery(GET_GROUP_FINDINGS, {
    fetchPolicy: "cache-first",
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        Logger.warning("An error occurred loading group data", error);
      });
    },
    variables: {
      groupName: subject,
    },
  });

  if (loading) {
    return (
      <Container
        alignItems={"center"}
        display={"flex"}
        justify={"center"}
        left={"50%"}
        position={"absolute"}
        top={"70%"}
      >
        <Loading size={"lg"} />
      </Container>
    );
  }

  if (_.isEmpty(data?.group.findings)) {
    return (
      <EmptyState
        description={t("analytics.emptyChart.description")}
        imageSrc={"integrates/empty/analyticsIcon"}
        padding={1.25}
        title={t("analytics.emptyChart.title")}
      />
    );
  }

  return (
    <CustomThemeProvider>
      <Container
        bgColor={theme.palette.gray[50]}
        height={"100hv"}
        px={1.25}
        py={1.25}
      >
        <Charts
          bgChange={searchParams.get("bgChange") === "true"}
          entity={"group"}
          reportMode={searchParams.get("reportMode") === "true"}
          subject={subject}
        />
      </Container>
    </CustomThemeProvider>
  );
};

export { GroupAnalytics };
